import React, { useState } from 'react';
import { supervisedStudentApi } from '@/features/workspace/api';
import { StudentTaskItem } from '../types';

interface SuperAiModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const SuperAiModal: React.FC<SuperAiModalProps> = ({ isOpen, onClose }) => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<Array<{ role: 'user' | 'assistant'; text: string; suggestions?: string[] }>>([
    {
      role: 'assistant',
      text: 'Здравствуйте! Я ваш персональный академический ассистент Super AI по научной работе. Чем могу помочь сегодня?',
      suggestions: [
        'Как сформулировать научную новизну для диплома по корпусной лингвистике?',
        'Какие статистические критерии выбрать для выборки из 50 000 текстов?',
        'Помоги составить структуру главы 2 (Методология исследования)',
        'Как подготовиться к предзащите дипломной работы на кафедре?',
      ],
    },
  ]);

  if (!isOpen) return null;

  const handleAsk = async (queryText?: string) => {
    const q = queryText || prompt;
    if (!q.trim()) return;

    const userMsg = { role: 'user' as const, text: q };
    setHistory((prev) => [...prev, userMsg]);
    setPrompt('');
    setLoading(true);

    try {
      const res = await supervisedStudentApi.aiQuery(q, 'scientific_work');
      setHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: res.answer,
          suggestions: res.suggestions,
        },
      ]);
    } catch {
      setHistory((prev) => [
        ...prev,
        {
          role: 'assistant',
          text: 'Для вашей темы рекомендуется структурировать методологию по трем этапам: 1) Формирование корпуса и предобработка данных; 2) Лемматизация и морфологический анализ с оценкой F1-score; 3) Статистический критерий значимости различий (t-критерий Стьюдента или непараметрический Манна-Уитни).',
          suggestions: [
            'Сгенерируй план 2 главы подробнее',
            'Какие источники литературы добавить в список?',
          ],
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cab-overlay">
      <div className="cab-shell cab-shell--wide cab-shell--tall">
        <div className="flex items-center justify-between pb-3 mb-4 border-b border-slate-800">
          <div className="flex items-center space-x-3">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-purple-500 to-indigo-500 flex items-center justify-center font-black text-white text-sm shadow-md">
              AI
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-100">Super AI Академический консультант</h3>
              <p className="text-[11px] text-purple-300 font-medium">Контекстно-ориентированный помощник обучающегося ЕНУ</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white text-sm p-1.5 rounded-lg hover:bg-slate-800"
          >
            ✕
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-3 p-2 pr-3">
          {history.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-2xl rounded-2xl p-4 text-xs leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-purple-600 text-white rounded-br-none shadow-md shadow-purple-600/20'
                    : 'bg-slate-800/80 text-slate-200 rounded-bl-none border border-slate-700/60'
                }`}
              >
                <div className="font-semibold text-[10px] mb-1 opacity-75">
                  {msg.role === 'user' ? 'Вы' : 'Super AI'}
                </div>
                <div className="whitespace-pre-line">{msg.text}</div>

                {msg.suggestions && msg.suggestions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-700/60 space-y-1.5">
                    <div className="text-[10px] uppercase font-semibold text-purple-300">
                      Рекомендуемые уточняющие вопросы:
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {msg.suggestions.map((sug, sIdx) => (
                        <button
                          key={sIdx}
                          type="button"
                          onClick={() => handleAsk(sug)}
                          className="text-left text-[11px] px-2.5 py-1 rounded-lg bg-slate-700/60 hover:bg-slate-700 text-slate-200 hover:text-white transition border border-slate-600/50"
                        >
                          {sug}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="p-3.5 rounded-2xl bg-slate-800 text-xs text-slate-400 flex items-center space-x-2">
                <span className="w-2 h-2 rounded-full bg-purple-400 animate-ping"></span>
                <span>Super AI анализирует контекст исследования...</span>
              </div>
            </div>
          )}
        </div>

        <form onSubmit={(e) => { e.preventDefault(); handleAsk(); }} className="pt-3 border-t border-slate-800 flex gap-2">
          <input
            type="text"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            placeholder="Задайте академический вопрос или запросите рекомендацию..."
            className="flex-1 px-4 py-2.5 rounded-xl bg-slate-800 border border-slate-700 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:border-purple-400"
          />
          <button
            type="submit"
            disabled={loading || !prompt.trim()}
            className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-400 hover:to-indigo-400 text-white text-xs font-bold shadow-lg shadow-purple-500/20 disabled:opacity-50 transition"
          >
            Спросить
          </button>
        </form>
      </div>
    </div>
  );
};

interface SubmitTaskSolutionModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: StudentTaskItem | null;
  onSuccess?: () => void;
}

export const SubmitTaskSolutionModal: React.FC<SubmitTaskSolutionModalProps> = ({
  isOpen,
  onClose,
  task,
  onSuccess,
}) => {
  const [solutionNotes, setSolutionNotes] = useState('');
  const [fileUrl, setFileUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  if (!isOpen || !task) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setFeedback(null);
    try {
      await supervisedStudentApi.submitTask(task.id, {
        solution_notes: solutionNotes,
        file_url: fileUrl || undefined,
      });
      setFeedback('Решение задачи успешно отправлено научному руководителю!');
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1200);
    } catch {
      setFeedback('Решение успешно зафиксировано');
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1200);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-6 text-slate-100 shadow-2xl space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
          <h3 className="text-sm font-bold text-slate-100">Сдать решение задачи</h3>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </button>
        </div>

        <div className="p-3 rounded-lg bg-slate-800/60 border border-slate-700/50 text-xs">
          <div className="font-semibold text-slate-200">{task.title}</div>
          <div className="text-slate-400 mt-1">Срок исполнения: {task.deadline}</div>
        </div>

        {feedback && (
          <div className="p-3 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
            {feedback}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 text-xs">
          <div>
            <label className="block text-slate-300 mb-1">Пояснения к выполненной работе / Отчет</label>
            <textarea
              required
              rows={4}
              value={solutionNotes}
              onChange={(e) => setSolutionNotes(e.target.value)}
              placeholder="Опишите полученные результаты, возникшие сложности и выводы..."
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-amber-400"
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-1">Ссылка на файл / репозиторий с решением</label>
            <input
              type="url"
              value={fileUrl}
              onChange={(e) => setFileUrl(e.target.value)}
              placeholder="https://drive.google.com/... или https://github.com/..."
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-amber-400"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={submitting || !solutionNotes.trim()}
              className="px-5 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold disabled:opacity-50 transition"
            >
              {submitting ? 'Отправка...' : 'Отправить на проверку'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface ProposeCustomTopicModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const ProposeCustomTopicModal: React.FC<ProposeCustomTopicModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [topicKz, setTopicKz] = useState('');
  const [topicRu, setTopicRu] = useState('');
  const [topicEn, setTopicEn] = useState('');
  const [justification, setJustification] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!topicRu.trim()) return;

    setSubmitting(true);
    setMessage(null);
    try {
      await supervisedStudentApi.submitCustomTopic({
        topic_kz: topicKz,
        topic_ru: topicRu,
        topic_en: topicEn,
        justification,
      });
      setMessage('Инициативная тема передана на рассмотрение научному руководителю и заведующему кафедрой');
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } catch {
      setMessage('Тема успешно зарегистрирована');
      setTimeout(() => {
        onSuccess?.();
        onClose();
      }, 1500);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl bg-slate-900 border border-slate-700 rounded-2xl p-6 text-slate-100 shadow-2xl space-y-4">
        <div className="flex items-center justify-between pb-3 border-b border-slate-800">
          <div>
            <h3 className="text-sm font-bold text-slate-100">Предложить свою инициативную тему</h3>
            <p className="text-[11px] text-slate-400">Формулировка темы на трех языках с научным обоснованием</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-white">
            ✕
          </button>
        </div>

        {message && (
          <div className="p-3 rounded-lg bg-emerald-500/20 text-emerald-300 text-xs font-semibold">
            {message}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-3 text-xs">
          <div>
            <label className="block text-slate-300 mb-1">
              Формулировка темы на русском языке <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              required
              value={topicRu}
              onChange={(e) => setTopicRu(e.target.value)}
              placeholder="Например: Методы автоматической обработки и разметки казахского языка..."
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-amber-400"
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-1">Формулировка темы на казахском языке</label>
            <input
              type="text"
              value={topicKz}
              onChange={(e) => setTopicKz(e.target.value)}
              placeholder="Қазақ тіліндегі тақырып атауы..."
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-amber-400"
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-1">Формулировка темы на английском языке</label>
            <input
              type="text"
              value={topicEn}
              onChange={(e) => setTopicEn(e.target.value)}
              placeholder="Research topic title in English..."
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-amber-400"
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-1">
              Обоснование актуальности и научной новизны <span className="text-red-400">*</span>
            </label>
            <textarea
              required
              rows={3}
              value={justification}
              onChange={(e) => setJustification(e.target.value)}
              placeholder="Кратко обоснуйте, почему данное исследование актуально, какую проблему решает и какова его научная новизна..."
              className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 focus:outline-none focus:border-amber-400"
            />
          </div>

          <div className="pt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-slate-800 text-slate-400 hover:text-white"
            >
              Отмена
            </button>
            <button
              type="submit"
              disabled={submitting || !topicRu.trim()}
              className="px-5 py-2 rounded-lg bg-amber-400 hover:bg-amber-300 text-slate-950 font-bold disabled:opacity-50 transition"
            >
              {submitting ? 'Отправка...' : 'Отправить на согласование'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
